import {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect
} from "react";
import { isClient } from "../global";

export interface DimensionObject {
  width: number;
  height: number;
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export type UseDimensionsHook<T> = [
  (node: T) => void,
  Partial<DimensionObject>,
  T | null
];

export interface UseDimensionsArgs {
  liveMeasure?: boolean;
}

function getDimensionObject<T extends HTMLElement>(node: T): DimensionObject {
  const rect = node.getBoundingClientRect();

  return {
    width: rect.width,
    height: rect.height,
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom
  };
}

// Credit to https://github.com/Swizec/useDimensions for the original work on this,
// we needed to tweak some of the types and add better support for SSR
function useDimensions<T extends HTMLElement>({
  liveMeasure = true
}: UseDimensionsArgs = {}): UseDimensionsHook<T> {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [dimensions, setDimensions] = useState<Partial<DimensionObject>>({
    width: undefined,
    height: undefined,
    top: undefined,
    left: undefined,
    right: undefined,
    bottom: undefined
  });
  const [node, setNode] = useState<T | null>(null);

  const ref = useCallback(node => {
    setNode(node);
  }, []);

  const requestRef = useRef<number>();

  // Use useEffect for SSR first render pass to suppress warning when using
  // useLayoutEffect on the server: https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85
  const useIsomorphicLayoutEffect = isClient() ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    if (node) {
      const measure = () =>
        window.requestAnimationFrame(() =>
          setDimensions(getDimensionObject(node))
        );
      requestRef.current = measure();

      if (liveMeasure) {
        if ("ResizeObserver" in window) {
          const resizeObserver = new ResizeObserver(measure);
          resizeObserver.observe(node);

          return () => {
            if (requestRef.current) {
              window.cancelAnimationFrame(requestRef.current);
            }
            resizeObserver.disconnect();
          };
        } else {
          window.addEventListener("resize", measure);
          window.addEventListener("scroll", measure);

          return () => {
            if (requestRef.current) {
              window.cancelAnimationFrame(requestRef.current);
            }
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure);
          };
        }
      }

      return () => {
        if (requestRef.current) {
          window.cancelAnimationFrame(requestRef.current);
        }
      };
    }
  }, [node, liveMeasure]);

  return [ref, dimensions, node];
}

export default useDimensions;
